<template>
  <div>
    <div class="btn-group-hdr">
      <div class="btn-group btn-group-toggle">
        <button class="btn btn-secondary btn-sm" @click="cancel">Cancel</button>
        <button class="btn btn-primary btn-sm" @click="confirmSave">Save</button>
      </div>
    </div>
    <div class="title-hdr bg-dark p-2 text-white">
      <p class="mb-0">
        Assign User Permissions:
        <span class="badge bg-success">{{ user.display_name }}</span>
      </p>
    </div>

    <!-- Outer tabs: Projects -->
    <ul class="nav nav-tabs" id="projectTab" role="tablist">
      <li
        v-for="(brands, project, pIndex) in groupedByProject"
        :key="project"
        role="presentation"
      >
        <button
          class="nav-link"
          :class="{ active: activeProjectTab === pIndex }"
          :id="`project-tab-${pIndex}`"
          data-toggle="tab"
          :data-target="`#project-tabpane-${pIndex}`"
          type="button"
          role="tab"
          :aria-controls="`project-tabpane-${pIndex}`"
          :aria-selected="activeProjectTab === pIndex ? 'true' : 'false'"
          @click="onProjectTabClick(pIndex)"
        >
          {{ project }}
        </button>
      </li>
    </ul>

    <div class="tab-content mt-1" id="projectTabContent">
      <div
        v-for="(brands, project, pIndex) in groupedByProject"
        :key="project"
        class="tab-pane fade"
        :class="{ show: activeProjectTab === pIndex, active: activeProjectTab === pIndex }"
        :id="`project-tabpane-${pIndex}`"
        role="tabpanel"
        :aria-labelledby="`project-tab-${pIndex}`"
      >
        <!-- Middle tabs: Brands -->
        <ul class="nav nav-tabs" id="brandTab" role="tablist">
          <li
            v-for="(countries, brand, bIndex) in brands"
            :key="brand"
            role="presentation"
          >
            <button
              class="nav-link"
              :class="{ active: activeBrandTabs[pIndex] === bIndex }"
              :id="`brand-tab-${pIndex}-${bIndex}`"
              data-toggle="tab"
              :data-target="`#brand-tabpane-${pIndex}-${bIndex}`"
              type="button"
              role="tab"
              :aria-controls="`brand-tabpane-${pIndex}-${bIndex}`"
              :aria-selected="activeBrandTabs[pIndex] === bIndex ? 'true' : 'false'"
              @click="onBrandTabClick(pIndex, bIndex)"
            >
              {{ brand }}
            </button>
          </li>
        </ul>

        <div class="tab-content mt-1" id="brandTabContent">
          <div
            v-for="(countries, brand, bIndex) in brands"
            :key="brand"
            class="tab-pane fade"
            :class="{ show: activeBrandTabs[pIndex] === bIndex, active: activeBrandTabs[pIndex] === bIndex }"
            :id="`brand-tabpane-${pIndex}-${bIndex}`"
            role="tabpanel"
            :aria-labelledby="`brand-tab-${pIndex}-${bIndex}`"
          >
            <!-- Inner tabs: Countries -->
            <ul class="nav nav-tabs" id="countryTab" role="tablist">
              <li
                v-for="(perms, country, cIndex) in countries"
                :key="country"
                role="presentation"
              >
                <button
                  class="nav-link"
                  :class="{ active: activeCountryTabs[`${pIndex}-${bIndex}`] === cIndex }"
                  :id="`country-tab-${pIndex}-${bIndex}-${cIndex}`"
                  data-toggle="tab"
                  :data-target="`#country-tabpane-${pIndex}-${bIndex}-${cIndex}`"
                  type="button"
                  role="tab"
                  :aria-controls="`country-tabpane-${pIndex}-${bIndex}-${cIndex}`"
                  :aria-selected="activeCountryTabs[`${pIndex}-${bIndex}`] === cIndex ? 'true' : 'false'"
                  @click="setActiveCountryTab(pIndex, bIndex, cIndex)"
                >
                  {{ country }}
                </button>
              </li>
            </ul>

            <div class="tab-content mt-1" id="countryTabContent">
              <div
                v-for="(perms, country, cIndex) in countries"
                :key="country"
                class="tab-pane fade"
                :class="{ show: activeCountryTabs[`${pIndex}-${bIndex}`] === cIndex, active: activeCountryTabs[`${pIndex}-${bIndex}`] === cIndex }"
                :id="`country-tabpane-${pIndex}-${bIndex}-${cIndex}`"
                role="tabpanel"
                :aria-labelledby="`country-tab-${pIndex}-${bIndex}-${cIndex}`"
              >
                <div class="ck-tbl-dynamix">
                  <table id="tbl-minmax" class="table table-bordered table-hover table-inline-edit">
                    <thead class="thead-dark">
                    <tr>
                      <th style="width: 40px; text-align: center;">
                        <div class="custom-control custom-checkbox">
                          <input
                            type="checkbox"
                            class="custom-control-input"
                            :checked="areAllPermissionsSelected(sortedPermissions(perms))"
                            @change="toggleAllPermissions(sortedPermissions(perms), $event.target.checked)"
                            :id="`select-all-perms-${pIndex}-${bIndex}-${cIndex}`"
                          />
                          <label
                            class="custom-control-label h4"
                            :for="`select-all-perms-${pIndex}-${bIndex}-${cIndex}`"
                          ></label>
                        </div>
                      </th>
                      <th class="h5">Permission Name</th>
                      <th class="h5">Description</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                      v-for="perm in sortedPermissions(perms)"
                      :key="perm.permission_id"
                      @click="toggleCheckbox(perm.permission_id)"
                      style="cursor: pointer;"
                    >
                      <td class="text-center" @click.stop>
                        <div class="custom-control custom-checkbox">
                          <input
                            type="checkbox"
                            class="custom-control-input"
                            :checked="selectedPermissionIds.includes(perm.permission_id) || isPermissionFromRole(perm.permission_id)"
                            :disabled="isPermissionFromRole(perm.permission_id)"
                            @change="toggleCheckbox(perm.permission_id)"
                            :id="`perm-checkbox-${perm.permission_id}`"
                          />
                          <label
                            class="custom-control-label h4"
                            :for="`perm-checkbox-${perm.permission_id}`"
                          ></label>
                        </div>
                      </td>
                      <td>
                        <label class="mb-0 font-weight-bold h6">{{ perm.permission_name }}</label>
                      </td>
                      <td class="h6">{{ perm.description }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AssignUserPermissions',
  props: {
    user: {
      type: Object,
      required: true
    },
    permissions: {
      type: Array,
      default: () => []
    },
    rolePermissions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      allPermissions: [],
      selectedPermissionIds: [],
      activeProjectTab: 0,
      activeBrandTabs: {}, // track active brand tab per project index
      activeCountryTabs: {} // track active country tab per project-brand key
    }
  },
  computed: {
    groupedByProject() {
      const grouped = {}
      this.allPermissions.forEach(perm => {
        if (!grouped[perm.project_name]) {
          grouped[perm.project_name] = {}
        }
        if (!grouped[perm.project_name][perm.brand]) {
          grouped[perm.project_name][perm.brand] = {}
        }
        if (!grouped[perm.project_name][perm.brand][perm.country]) {
          grouped[perm.project_name][perm.brand][perm.country] = []
        }
        grouped[perm.project_name][perm.brand][perm.country].push(perm)
      })

      // Sort keys with priority helper
      function sortKeysWithPriority(keys, priority) {
        const prioritySet = new Set(priority)
        const priorityKeys = keys.filter(k => prioritySet.has(k))
        const otherKeys = keys.filter(k => !prioritySet.has(k)).sort()
        return [...priorityKeys, ...otherKeys]
      }

      // Sort projects: User Management first
      const sortedProjects = sortKeysWithPriority(Object.keys(grouped), ['User Management'])

      const sortedGrouped = {}

      sortedProjects.forEach(project => {
        const brands = grouped[project]
        // Sort brands: Charles & Keith first
        const sortedBrands = sortKeysWithPriority(Object.keys(brands), ['Charles & Keith'])

        sortedGrouped[project] = {}

        sortedBrands.forEach(brand => {
          const countries = brands[brand]
          // Sort countries: Singapore first
          const sortedCountries = sortKeysWithPriority(Object.keys(countries), ['Singapore'])

          sortedGrouped[project][brand] = {}

          sortedCountries.forEach(country => {
            sortedGrouped[project][brand][country] = countries[country]
          })
        })
      })

      return sortedGrouped
    }
  },
  mounted() {
    this.fetchAllPermissions()
  },
  methods: {
    fetchAllPermissions() {
      axios.get('/user/permissions') // Adjust API endpoint to fetch all permissions for users
        .then(res => {
          if (res.data && res.data.permissions) {
            this.allPermissions = res.data.permissions
            this.selectedPermissionIds = this.permissions.map(p => p.permission_id)

            // Initialize active tabs
            this.activeProjectTab = 0
            const projects = Object.keys(this.groupedByProject)
            projects.forEach((project, pIndex) => {
              this.$set(this.activeBrandTabs, pIndex, 0)
              const brands = Object.keys(this.groupedByProject[project])
              brands.forEach((brand, bIndex) => {
                const key = `${pIndex}-${bIndex}`
                this.$set(this.activeCountryTabs, key, 0)
              })
            })
          } else {
            this.allPermissions = []
          }
        })
        .catch(err => {
          console.error('Failed to fetch permissions:', err)
          this.allPermissions = []
        })
    },
    isPermissionFromRole(permissionId) {
      return this.rolePermissions.some(p => p.permission_id === permissionId)
    },
    toggleAllPermissions(perms, checked) {
      // Filter out permissions that come from role (cannot toggle)
      const filteredPerms = perms.filter(p => !this.isPermissionFromRole(p.permission_id))
      const permIds = filteredPerms.map(p => p.permission_id)
      if (checked) {
        this.selectedPermissionIds = Array.from(new Set([...this.selectedPermissionIds, ...permIds]))
      } else {
        this.selectedPermissionIds = this.selectedPermissionIds.filter(id => !permIds.includes(id))
      }
    },
    sortedPermissions(perms) {
      return perms.slice().sort((a, b) => {
        const nameA = a.permission_name.toLowerCase()
        const nameB = b.permission_name.toLowerCase()
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      })
    },
    areAllPermissionsSelected(perms) {
      // Only consider permissions that are not from role for "all selected" check
      const filteredPerms = perms.filter(p => !this.isPermissionFromRole(p.permission_id))
      if (filteredPerms.length === 0) return true // If none to select, consider all selected
      return filteredPerms.every(perm => this.selectedPermissionIds.includes(perm.permission_id))
    },
    toggleCheckbox(permissionId) {
      if (this.isPermissionFromRole(permissionId)) {
        // Do nothing if permission is from role (disabled)
        return
      }
      const index = this.selectedPermissionIds.indexOf(permissionId)
      if (index === -1) {
        this.selectedPermissionIds.push(permissionId)
      } else {
        this.selectedPermissionIds.splice(index, 1)
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    confirmSave() {
      Swal.fire({
        title: 'Are you sure?',
        text: "Do you want to save the permission changes?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, save it!',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        console.log('Swal result:', result)
        if (result.value) {  // in v8, result.value === true means confirmed
          console.log('User confirmed save')
          this.savePermissions()
        } else {
          console.log('User cancelled save')
        }
      })
    },
    savePermissions() {
      this.$emit('save', {
        userId: this.user.user_id,
        permissions: this.selectedPermissionIds
      })
    },
    onProjectTabClick(pIndex) {
      this.activeProjectTab = pIndex
      if (this.activeBrandTabs[pIndex] === undefined) {
        this.$set(this.activeBrandTabs, pIndex, 0)
      }
      const brands = Object.keys(this.groupedByProject[Object.keys(this.groupedByProject)[pIndex]])
      if (brands.length > 0) {
        const key = `${pIndex}-0`
        if (this.activeCountryTabs[key] === undefined) {
          this.$set(this.activeCountryTabs, key, 0)
        }
      }
    },
    onBrandTabClick(pIndex, bIndex) {
      this.$set(this.activeBrandTabs, pIndex, bIndex)
      const key = `${pIndex}-${bIndex}`
      if (this.activeCountryTabs[key] === undefined) {
        this.$set(this.activeCountryTabs, key, 0)
      }
    },
    setActiveCountryTab(pIndex, bIndex, cIndex) {
      const key = `${pIndex}-${bIndex}`
      this.$set(this.activeCountryTabs, key, cIndex)
    }
  },
  watch: {
    permissions: {
      immediate: true,
      handler(newPermissions) {
        this.selectedPermissionIds = newPermissions.map(p => p.permission_id)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/users/users/AssignUserPermissions.vue